/* prettier-ignore */

import { CommonModule } from '@angular/common';
import { Component, effect, ElementRef, inject, input, InputSignal, ViewChild } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Chart, LineController, LineElement, PointElement, LinearScale, CategoryScale, Tooltip, Legend, ChartConfiguration, ChartOptions, Filler } from 'chart.js';
import { Subscription } from 'rxjs';
Chart.register(LineController, LineElement, PointElement, LinearScale, CategoryScale, Tooltip, Legend, Filler);

@Component({
    selector: "app-details-gpx-elevation",
    imports: [
        CommonModule,
        TranslateModule,
    ],  
    standalone: true,
    templateUrl: "./elevation.component.html",
    styleUrls: ["./elevation.component.scss"],
})

export class ElevationComponent {
    public elevationData: InputSignal<any> = input<any>();
    //public gpxStatsData: InputSignal<any> = input<any>();
    public totalDistance: InputSignal<number> = input<number>(); 
    private translate = inject(TranslateService);
    private langSub?: Subscription;
    
    @ViewChild('elevationCanvas') elevationCanvas!: ElementRef<HTMLCanvasElement>;
    private elevationChart?: Chart<'line'>;
    public lineChartOptions: ChartOptions<'line'> = {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        interaction: {
            mode: 'index',
            intersect: false,
            axis: 'x',
        },
        plugins: {
            legend: {
                display: false,
            },
            tooltip: { 
                enabled: true ,
                intersect: false,
                mode: 'nearest',
                callbacks: {
                    title: (items) => {
                        const x = items[0].parsed.x;
                        return `${this.translate.instant('Distancia')}: ${Number(x).toFixed(2)} km`;
                    },
                    label: (item) => {
                        const y = item.parsed.y;
                        return `${this.translate.instant('Altura')}: ${Number(y).toFixed(0)} m`;
                    },
                },
            }
        },
        scales: {
            x: {
                type: 'linear',
                title: {
                    display: true,
                    text: `${this.translate.instant('Distancia')} (km)`
                },
                ticks: {
                    display: true,
                    autoSkip: true, 
                    stepSize: 1,
                    // maxTicksLimit: 15,
                    callback: (v) => `${Number(v).toFixed(0)} km`,
                },
                grid: {
                    drawOnChartArea: false
                }
            },
            y: {
                title: {
                    display: true,
                    text: `${this.translate.instant('Elevación')} (m)`,  
                },
                ticks: {
                    display: true,
                    callback: (value) => `${value} m`, 
                },
            },
        }
    };

    ngOnInit(): void {
        this.applyTranslations();
        this.langSub = this.translate.onLangChange.subscribe(() => {
            this.applyTranslations();
            if (this.elevationChart) 
                this.elevationChart.update();
        });
    }

    gpxElevationEffect = effect(() => {
        const gpxElevation = this.elevationData();
        //const gpxStats = this.gpxStatsData();
        if (!gpxElevation)
            return;

        this.updateElevationChart(gpxElevation);
    })

    private updateElevationChart(gpxElevation): void {
        if (!this.elevationCanvas) 
            return;

        const ctx = this.elevationCanvas.nativeElement.getContext('2d');
        if (!ctx) 
            return;

        const data = gpxElevation.map(p => ({ 
            x: p.distance,
            y: p.elevation
        }));

        const maxDistance = data[data.length - 1].x;
        const options: ChartOptions<'line'> = {
            ...this.lineChartOptions,
            scales: {
                ...this.lineChartOptions.scales,
                x: {
                    ...(this.lineChartOptions.scales?.x as any),
                    min: 0,
                    max: maxDistance,
                },
            }
        };
        const chartData: ChartConfiguration<'line'>['data'] = {
            // labels,
            datasets: [
                {
                    label: `${this.translate.instant('Elevación')} (m)`,
                    data,
                    borderWidth: 2,
                    borderColor: '#ed5a64',
                    backgroundColor: '#ffe8ea',
                    fill: true,
                    pointRadius: 0,
                    tension: 0.25,
                },
            ]
        };

        if (this.elevationChart) {
            this.elevationChart.data = chartData;
            this.elevationChart.update();
        } else {
            this.elevationChart = new Chart(ctx, {
                type: 'line',
                data: chartData,
                options: options
            });
        }
    }

    private applyTranslations(): void {
        const distanceLabel = this.translate.instant('Distancia');
        const elevationLabel = this.translate.instant('Elevación');

        const xScale = this.lineChartOptions.scales?.x as any;
        if (xScale?.title) 
            xScale.title.text = `${distanceLabel} (km)`;

        const yScale = this.lineChartOptions.scales?.y as any;
        if (yScale?.title) 
            yScale.title.text = `${elevationLabel} (m)`;

        if (!this.elevationChart)
            return;

        const chartXScale = this.elevationChart.options.scales?.x as any;
        if (chartXScale?.title) 
            chartXScale.title.text = `${distanceLabel} (km)`;

        const chartYScale = this.elevationChart.options.scales?.y as any;
        if (chartYScale?.title) 
            chartYScale.title.text = `${elevationLabel} (m)`;

        const dataset = this.elevationChart.data.datasets?.[0];
        if (dataset) 
            dataset.label = `${elevationLabel} (m)`;
    }

    ngOnDestroy(): void {
        if (this.langSub) 
            this.langSub.unsubscribe();
        if (this.elevationChart) 
            this.elevationChart.destroy();
    }
}
